import React from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import {
  ArrowDropDown,
  ArrowDropUp,
  Menu,
  MenuOpen
} from '@mui/icons-material';
import {
  Box,
  CardMedia,
  IconButton,
  IconButtonProps,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  StackProps,
  SxProps,
  Theme,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';

import CurrentUser from '@/components/CurrentUser';
import FacilitySelector from '@/components/FacilitySelector';
import { EXACARE_LOGOS_S3_BUCKET_URLS } from '@/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsLandscapeMobileViewport } from '@/hooks/useIsLandscapeMobileViewport';
import { usePylonChat } from '@/hooks/usePylonChat';
import { useSidebarExpandedAtom } from '@/hooks/useSidebarExpandedAtom';
import InboxModal from '@/pages/AdminHub/Staff/Notifications/components/InboxModal';

import { SidebarItem, useGetSidebarCRMItems } from './getSidebarCRMItems';
import HelpCenterButton from './HelpCenterButton';
import { SIDEBAR_COLORS, thinScrollbarSx } from './sidebarTheme';

const SIDEBAR_NAV_BUTTON_SIZE = 48;
const SIDEBAR_HEADER_BUTTON_SIZE = 30;
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 64;

export const StyledNavButton = styled(ListItemButton)({
  minHeight: SIDEBAR_NAV_BUTTON_SIZE,
  height: SIDEBAR_NAV_BUTTON_SIZE,
  borderRadius: '100px',
  '&.Mui-disabled': {
    opacity: 1
  },
  '&.Mui-selected': {
    '&:hover': {
      background: '#D3E5F4'
    },
    background: '#D3E5F4'
  }
});

export const StyledNavButtonOutlined = styled(StyledNavButton)({
  border: '1px solid #D4DEE7',
  '& .MuiListItemAvatar-root': {
    minWidth: 'unset'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 'unset',
    '& .MuiSvgIcon-root': {
      color: '#364955'
    }
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    color: SIDEBAR_COLORS.textPrimary
  },
  '& .MuiListItemText-secondary': {
    fontSize: '12px!important',
    color: SIDEBAR_COLORS.textSecondary
  },
  '& .MuiTypography-root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

const StyledCollapseIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})<IconButtonProps & { expanded: boolean }>(({ expanded }) => ({
  width: `${SIDEBAR_HEADER_BUTTON_SIZE}px`,
  height: `${SIDEBAR_HEADER_BUTTON_SIZE}px`,
  padding: 0,
  '& svg': { fontSize: '20px' },
  ...(expanded ? { top: '1px', left: '4px' } : {})
}));

const StyledNavStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})<StackProps & { expanded: boolean }>(({ expanded }) => ({
  flexDirection: 'column',
  width: expanded
    ? `${SIDEBAR_WIDTH_EXPANDED}px`
    : `${SIDEBAR_WIDTH_COLLAPSED}px`,
  transition: 'width 200ms ease-in-out',
  marginTop: expanded ? '8px' : 0
}));

const StyledNavScrollStack = styled(Stack)({
  overflow: 'auto',
  flex: 1,
  minHeight: 0,
  ...thinScrollbarSx
});

const StyledNavFooterList = styled(List)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  [theme.breakpoints.up('sm')]: {
    padding: `${theme.spacing(1.5)} ${theme.spacing(1)}`,
    gap: '6px'
  },
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
    gap: '8px'
  }
}));

export const getCollapsibleButtonSx = (
  expanded: boolean,
  variant: 'default' | 'avatar' = 'default'
): SxProps<Theme> => ({
  width: expanded ? '100%' : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  height: `${SIDEBAR_NAV_BUTTON_SIZE}px`,
  minWidth: expanded ? undefined : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  maxWidth: expanded ? undefined : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  justifyContent: expanded ? 'flex-start' : 'center',
  padding: expanded
    ? variant === 'avatar'
      ? '6px 16px 6px 8px'
      : '6px 16px'
    : '0 !important',
  gap: expanded ? '8px' : undefined
});

export const collapsibleItemSx: SxProps<Theme> = {
  minWidth: 'unset',
  margin: 0,
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export const getCollapsibleIconSx = (
  expanded: boolean,
  expandedMarginRight = '12px'
): SxProps<Theme> => ({
  minWidth: 'unset',
  marginRight: expanded ? expandedMarginRight : '0 !important'
});

export const getNavButtonSx = (expanded: boolean): SxProps<Theme> => ({
  width: expanded ? '100%' : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  minWidth: expanded ? undefined : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  maxWidth: expanded ? undefined : `${SIDEBAR_NAV_BUTTON_SIZE}px !important`,
  justifyContent: expanded ? 'flex-start' : 'center',
  padding: expanded ? undefined : '0 !important',
  height: `${SIDEBAR_NAV_BUTTON_SIZE}px`,
  minHeight: `${SIDEBAR_NAV_BUTTON_SIZE}px`,
  borderRadius: expanded ? '100px' : '50%'
});

interface DropdownArrowProps {
  isOpen: boolean;
}

export const DropdownArrow: React.FC<DropdownArrowProps> = ({ isOpen }) =>
  isOpen ? <ArrowDropUp /> : <ArrowDropDown />;

interface SidebarListItemProps {
  expanded: boolean;
  children: React.ReactNode;
}

const SidebarListItem: React.FC<SidebarListItemProps> = ({
  expanded,
  children
}) => (
  <ListItem
    disablePadding
    sx={{
      display: 'flex',
      justifyContent: expanded ? 'flex-start' : 'center',
      width: '100%'
    }}
  >
    {children}
  </ListItem>
);

interface InboxModalWrapperProps {
  userId: string;
  collapsed?: boolean;
}

const InboxModalWrapper: React.FC<InboxModalWrapperProps> = ({
  userId,
  collapsed
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: collapsed ? '12px' : 0,
      marginTop: 0,
      '& .ss-feed-bell-container': {
        bottom: collapsed ? undefined : 3
      },
      '& svg': {
        fontSize: '20px'
      },
      '& p': {
        top: '-10px !important',
        right: '-14px !important'
      }
    }}
  >
    <InboxModal userId={userId} />
  </Box>
);

/**
 * The section a path belongs to: its domain + section segment, e.g.
 * `/crm/data-center/dashboards` → `/crm/data-center`. Every sidebar nav target
 * lives inside its own section, so by default an item lights up for its whole
 * section and any child route is covered without enumerating it here.
 */
const getSectionBase = (path: string): string =>
  `/${path.split('/').filter(Boolean).slice(0, 2).join('/')}`;

/**
 * Route patterns that mark a sidebar item active. Defaults to the item's
 * section (see `getSectionBase`); `activePaths` overrides this for the rare tab
 * that is a sub-section of another tab's section and must out-specify it (e.g.
 * Authorizations / eSign both live under Screener's `/crm/screener`).
 */
const getActivePatterns = (item: SidebarItem): string[] =>
  item.activePaths ?? [getSectionBase(item.path)];

/**
 * Resolves which sidebar item is active for the current pathname.
 *
 * Each item's patterns are matched against the pathname as route prefixes
 * (react-router `matchPath`, `end: false`). When several match — e.g. the
 * Screener section (`/crm/screener`) and a more specific sub-tab like
 * eSign (`/crm/screener/esign`) — the longest match wins, i.e. the most
 * specific item. This keeps overlapping prefixes from lighting up two tabs at
 * once without per-route special cases.
 *
 * Returns the active item's `path`, or undefined if nothing matches.
 */
export const getActiveSidebarPath = (
  items: SidebarItem[],
  pathname: string
): string | undefined => {
  let activePath: string | undefined;
  let bestMatchLength = -1;

  for (const item of items) {
    for (const pattern of getActivePatterns(item)) {
      const match = matchPath({ path: pattern, end: false }, pathname);
      if (match && match.pathnameBase.length > bestMatchLength) {
        bestMatchLength = match.pathnameBase.length;
        activePath = item.path;
      }
    }
  }

  return activePath;
};

const Sidebar = () => {
  const currentUser = useCurrentUser().data!;
  const { unreadMessageCount, toggleChat } = usePylonChat();
  const isLandscapeMobile = useIsLandscapeMobileViewport();
  const pathname = useLocation().pathname;
  const crmSidebarItems = useGetSidebarCRMItems();
  const activePath = getActiveSidebarPath(crmSidebarItems, pathname);

  const isMediaQueryDownLg = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('lg')
  );
  const isMediaQueryDownMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const [expanded, setExpanded] = useSidebarExpandedAtom();

  React.useEffect(() => {
    setExpanded(!(isMediaQueryDownLg || isMediaQueryDownMd));
  }, [isMediaQueryDownLg, isMediaQueryDownMd, setExpanded]);

  return (
    <StyledNavStack component="nav" expanded={expanded}>
      <List dense disablePadding>
        <Stack
          padding={
            expanded && !isMediaQueryDownMd ? '8px 16px' : '16px 16px 6px'
          }
          width="100%"
          direction={isMediaQueryDownMd || !expanded ? 'column' : 'row'}
          alignItems="center"
          justifyContent={expanded ? 'flex-start' : 'center'}
          gap={expanded ? 1 : 0.5}
        >
          <StyledCollapseIconButton
            expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <MenuOpen /> : <Menu />}
          </StyledCollapseIconButton>
          {expanded && (
            <CardMedia
              component="img"
              src={EXACARE_LOGOS_S3_BUCKET_URLS.fullBlackAndWhite}
              sx={{ width: 'auto', height: '18px', objectFit: 'contain' }}
            />
          )}
          {!isLandscapeMobile && expanded && (
            <InboxModalWrapper userId={currentUser.id} />
          )}
          {!isLandscapeMobile && !expanded && (
            <InboxModalWrapper userId={currentUser.id} collapsed />
          )}
        </Stack>
      </List>
      <Box sx={{ px: expanded ? 1 : 0, pt: 1, pb: 1.5, flexShrink: 0 }}>
        <SidebarListItem expanded={expanded}>
          <FacilitySelector {...{ expanded }} />
        </SidebarListItem>
      </Box>
      <StyledNavScrollStack>
        <List dense disablePadding sx={{ px: expanded ? 1 : 0 }}>
          {crmSidebarItems.map((item, index) => {
            const { icon, text, path, onClick } = item;
            return (
              <SidebarListItem expanded={expanded} key={index}>
                <Tooltip
                  title={expanded ? '' : text}
                  placement="right"
                  disableInteractive
                >
                  <StyledNavButton
                    // @ts-ignore
                    component={onClick ? 'button' : Link}
                    to={onClick ? undefined : path}
                    onClick={onClick}
                    selected={item.path === activePath}
                    sx={getNavButtonSx(expanded)}
                  >
                    <ListItemIcon
                      sx={{
                        ...getCollapsibleIconSx(expanded, '4px'),
                        '& svg': { fontSize: '20px' }
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    {expanded && (
                      <ListItemText
                        sx={{ paddingLeft: 1 }}
                        primaryTypographyProps={{
                          whiteSpace: 'nowrap',
                          fontSize: '14px',
                          fontWeight: item.path === activePath ? 700 : 500,
                          color: SIDEBAR_COLORS.textPrimary
                        }}
                        primary={text}
                      />
                    )}
                  </StyledNavButton>
                </Tooltip>
              </SidebarListItem>
            );
          })}
        </List>
      </StyledNavScrollStack>
      {/* Bottom buttons - outside scrollable area to always stick to bottom */}
      <StyledNavFooterList dense disablePadding>
        <SidebarListItem expanded={expanded}>
          <HelpCenterButton
            expanded={expanded}
            unreadMessageCount={unreadMessageCount}
            onToggleChat={toggleChat}
          />
        </SidebarListItem>
        <SidebarListItem expanded={expanded}>
          <CurrentUser {...{ expanded }} />
        </SidebarListItem>
      </StyledNavFooterList>
    </StyledNavStack>
  );
};

export default Sidebar;
